import {
  programsNewCollection,
  selectDeviceEligibilityMessages,
  selectDeviceIneligibilityMessages,
  selectProgramDeviceLimits,
  selectProgramDevices,
  selectProgramRewardDeviceLimits,
} from '@/reducers/programsNew'
import { useEffect, useState } from 'react'
import { useProgramRequirements } from '@/app/hooks/useProgramRequirements'
import { useAppDispatch, useAppSelector } from '@/hooks'
import {
  selectInProgressEnrollment,
  setSelectedDevices,
} from '@/reducers/inProgressUtilityProgramEnrollments'
import { useNavigation } from '@/app/hooks'
import { useBottomSheet } from '@/context/bottom-sheet-context'
import { VehicleEligibilityRequirement } from '@/types/programs-new/vehicle-requirements'
import { useMultiProgramRouteContext } from '..'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'
import DeviceLimitHelpers from '@/app/pages/multi-program-enrollment/device-selection/DeviceLimitHelpers'
import useUtilityProgramRewards from '@/app/hooks/useUtilityProgramRewards'

export type UseViewModelProps = ReturnType<typeof useViewModel>

export function useViewModel() {
  const { utilityProgram } = useMultiProgramRouteContext()
  const { data: programs } = programsNewCollection.useFetch()
  const { rewardsByDeviceType, rewardsByCategory, deviceIncentives } =
    useUtilityProgramRewards(utilityProgram.id)
  const navigate = useNavigation()
  const { snapToHeader, closeBottomSheet } = useBottomSheet()
  const deviceIneligibility = useAppSelector(
    selectDeviceIneligibilityMessages(utilityProgram.id),
  )
  const deviceEligibility = useAppSelector(
    selectDeviceEligibilityMessages(utilityProgram.id),
  )
  const inProgressProgram = useAppSelector(
    selectInProgressEnrollment(utilityProgram.id),
  )
  const dispatch = useAppDispatch()
  const { routeToNextStep } = useProgramRequirements(utilityProgram)
  const { setFlowStartPoint } = useMultiProgramFlowControl()
  const updateDevices = (deviceUpdate: {
    vehicleIds?: number[]
    chargerIds?: number[]
    thermostatIds?: number[]
  }) => {
    dispatch(
      setSelectedDevices({
        utilityProgramId: utilityProgram.id,
        selectedDevices: {
          vehicleIds: deviceUpdate.vehicleIds ?? [],
          chargerIds: deviceUpdate.chargerIds ?? [],
          thermostatIds: deviceUpdate.thermostatIds ?? [],
        },
      }),
    )
  }

  const [selectedEligibleThermostatIds, setSelectedEligibleThermostatIds] =
    useState<number[]>(
      inProgressProgram?.selectedDevices.thermostatIds ??
        deviceEligibility?.thermostats.filter((t) => t?.id).map((t) => t.id) ??
        [],
    )
  const [selectedEligibleVehicleIds, setSelectedEligibleVehicleIds] = useState<
    number[]
  >(
    inProgressProgram?.selectedDevices.vehicleIds ??
      deviceEligibility?.vehicles.filter((v) => v?.id).map((v) => v.id) ??
      [],
  )
  const [selectedEligibleChargerIds, setSelectedEligibleChargerIds] = useState<
    number[]
  >(
    inProgressProgram?.selectedDevices.chargerIds ??
      deviceEligibility?.chargers.filter((c) => c?.id).map((c) => c.id) ??
      [],
  )

  const toggleVehicleEligibility = (id: number) => {
    setSelectedEligibleVehicleIds((prev) =>
      prev.includes(id) ? prev.filter((vid) => vid !== id) : [...prev, id],
    )
  }

  const toggleThermostatEligibility = (id: number) => {
    setSelectedEligibleThermostatIds((prev) =>
      prev.includes(id) ? prev.filter((tid) => tid !== id) : [...prev, id],
    )
  }

  const toggleChargerEligibility = (id: number) => {
    setSelectedEligibleChargerIds((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id],
    )
  }

  const onContinueClick = () => {
    updateDevices({
      chargerIds: selectedEligibleChargerIds,
      thermostatIds: selectedEligibleThermostatIds,
      vehicleIds: selectedEligibleVehicleIds,
    })
    routeToNextStep()
  }
  const devices = useAppSelector(selectProgramDevices(utilityProgram.id))
  const deviceLimits = useAppSelector(
    selectProgramDeviceLimits(utilityProgram.id),
  )
  const vehicles = devices.vehicles
  const thermostats = devices.thermostats
  const chargers = devices.chargers

  const rewardDeviceLimits = useAppSelector(
    selectProgramRewardDeviceLimits(utilityProgram.id),
  )

  // reset selected vehicles if any changes are made to the eligible vehicles
  useEffect(() => {
    if (programs.length === 0) {
      return
    }
    if (
      inProgressProgram?.selectedDevices.vehicleIds.some(
        (d) => !vehicles.find((vehicle) => vehicle?.id === d),
      )
    ) {
      setSelectedEligibleVehicleIds([])
      updateDevices({ vehicleIds: [] })
    }
  }, [vehicles, programs])

  // reset selected thermostats if any changes are made to the eligible thermostats
  useEffect(() => {
    if (programs.length === 0) {
      return
    }
    if (
      inProgressProgram?.selectedDevices.thermostatIds.some(
        (d) => !thermostats?.find((thermostat) => thermostat?.id === d),
      )
    ) {
      setSelectedEligibleThermostatIds([])
      updateDevices({ thermostatIds: [] })
    }
  }, [thermostats, programs])

  // reset selected chargers if any changes are made to the eligible chargers
  useEffect(() => {
    if (programs.length === 0) {
      return
    }
    if (
      inProgressProgram?.selectedDevices.chargerIds.some(
        (d) => !chargers?.find((charger) => charger?.id === d),
      )
    ) {
      setSelectedEligibleChargerIds([])
      updateDevices({ chargerIds: [] })
    }
  }, [chargers, programs])

  const eligibleDeviceCount =
    (deviceEligibility?.vehicles?.length ?? 0) +
    (deviceEligibility?.thermostats?.length ?? 0) +
    (deviceEligibility?.chargers?.length ?? 0)
  const ineligibleDeviceCount =
    (deviceIneligibility?.vehicles?.length ?? 0) +
    (deviceIneligibility?.thermostats?.length ?? 0) +
    (deviceIneligibility?.chargers?.length ?? 0)

  const hasFixableIneligibleVehicles =
    deviceIneligibility?.vehicles
      .flatMap((vehicle) => vehicle.requirements)
      .some(
        (v) => v.key === VehicleEligibilityRequirement.HAS_READ_PERMISSIONS,
      ) ?? false

  const initialTabIndex = hasFixableIneligibleVehicles ? 0 : 1

  const onAddDevice = () => {
    snapToHeader()
    closeBottomSheet()

    setFlowStartPoint(location.pathname, utilityProgram.id)
    navigate.push('/connect-device?utilityProgramId=' + utilityProgram.id)
  }

  const filteredVehicles = devices.vehicles.filter((vehicle) =>
    Boolean(vehicle),
  )
  const filteredThermostats = devices.thermostats.filter((thermostat) =>
    Boolean(thermostat),
  )
  const filteredChargers = devices.chargers.filter((charger) =>
    Boolean(charger),
  )
  const deviceLimitsResult = DeviceLimitHelpers.UserMeetsDeviceMinimumLimits(
    filteredVehicles?.length ?? 0,
    filteredThermostats?.length ?? 0,
    filteredChargers?.length ?? 0,
    0, // TODO AVild - Energy Systems in Multi Program
    false, // There is no behavioral device selection at this point
    deviceLimits,
  )
  const selectedDevicesMeetLimits = DeviceLimitHelpers.UserWithinDeviceLimits(
    selectedEligibleVehicleIds?.length ?? 0,
    selectedEligibleThermostatIds?.length ?? 0,
    selectedEligibleChargerIds?.length ?? 0,
    0, // TODO AVild - Energy Systems in Multi Program,
    false, // There is no behavioral device selection at this point
    deviceLimits,
  )

  return {
    utilityProgramId: utilityProgram.id,
    selectedEligibleThermostatIds,
    selectedEligibleVehicleIds,
    selectedEligibleChargerIds,
    toggleVehicleEligibility,
    toggleThermostatEligibility,
    toggleChargerEligibility,
    onContinueClick,
    utilityProgram,
    initialTabIndex,
    deviceIneligibility,
    deviceEligibility,
    eligibleDeviceCount,
    ineligibleDeviceCount,
    onAddDevice,
    deviceIncentives,
    hasFixableIneligibleVehicles,
    devices,
    deviceLimits,
    deviceLimitsResult,
    selectedDevicesMeetLimits: selectedDevicesMeetLimits.limitsMet,
    rewardsByDeviceType,
    rewardsByCategory,
    rewardDeviceLimits,
  }
}

export default useViewModel
